// NOTE: edit-comment modal is on parent TimelineFeed component; delete-comment modal is on this component
import * as React from "react"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { Tag } from "components-design-system"
import MenuDropdown from "components/TimelineFeed/MenuDropdown/MenuDropdown"
import Modal from "components/Modal/Modal"
import "./TimelineFeedItem.scss"

// api
import { useDownloadAttachment } from "api/hooks/documents/useDocumentsApi"
import { useUserPermissions } from "api/hooks"
import { useDeleteTimelineComment } from "api/hooks/studies/useTimelineFeedApi"
import { useDeleteActivityComment } from "api/hooks/activities/useActivitiesApi"
import { useDeleteRequestFeedComment } from "api/hooks/requests/useRequestTimelineApi"

const TimelineFeedItem = ({ type, data, onEditComment, refetchTimeline, isLastItem, isDateRepeat }) => {
  /** menu items */
  const { data: permissions } = useUserPermissions()
  const canEdit = permissions.user_id === data.author_id || permissions?.["study.comment.edit"] // NOTE: user can edit their own comments or, if admin, can edit anyone's comments
  const canDelete = permissions?.["study.comment.delete"] === true || permissions?.["project.comment.delete"] === true

  /** attachments */

  const [selectedAttachment, setSelectedAttachment] = React.useState(null)
  const { data: downloadAttachment } = useDownloadAttachment(selectedAttachment)

  React.useEffect(() => {
    if (downloadAttachment) {
      window.open(downloadAttachment, "_blank")
      setSelectedAttachment(null)
    }
  }, [downloadAttachment])

  /** delete */

  const { studyId, activityId, requestId } = useParams()
  const deleteStudyComment = useDeleteTimelineComment(studyId)
  const deleteActivityComment = useDeleteActivityComment(activityId)
  const deleteRequestComment = useDeleteRequestFeedComment(requestId)
  const [openDeleteComment, setOpenDeleteComment] = React.useState(null)

  const deleteComment = React.useMemo(() => {
    if (type === "study") return deleteStudyComment
    else if (type === "activity") return deleteActivityComment
    else if (type === "request") return deleteRequestComment
  }, [data, type])

  const onDeleteComment = () => {
    deleteComment(
      { id: data.id + "/" },
      {
        onSuccess: () => {
          refetchTimeline()
          setOpenDeleteComment(false)
        },
      }
    )
  }

  /** menu items */
  const menuItems = []
  if (canEdit) {
    menuItems.push({
      key: "1",
      label: "Edit",
      onClick: () => onEditComment(data),
    })
  }
  if (canDelete) {
    menuItems.push({
      key: "2",
      label: "Delete",
      onClick: () => setOpenDeleteComment(true),
    })
  }

  // used both for item list and delete modal
  const mainContent = (
    <>
      {/* eyebrow - user and date */}
      <div className="wf-feed-item-content-eyebrow">
        {data?.author_fullname}, {data?.action_date ? dayjs(data.action_date).format("h:mm A") : null}
      </div>
      {/* title */}
      <div className="wf-feed-item-content-title">{data?.timeline_title}</div>
      {/* main text */}
      <div className="wf-feed-item-content-main">{data?.timeline_text}</div>
    </>
  )

  return (
    // NOTE: class for item_type "study_event" changed to "study-event"
    <li
      className={`workflow-feed-item${isLastItem ? " workflow-feed-item-last" : ""}${
        data?.item_type
          ? ` workflow-feed-item-${data.item_type.replace("_", "-")}${isDateRepeat ? " workflow-feed-item-repeat" : ""}`
          : ""
      }`}
    >
      {/* tail */}
      <div className="wf-feed-item-tail" />

      {/* head */}
      <div className="wf-feed-item-head">
        <div className="wf-feed-item-dayofweek">{data?.action_date ? dayjs(data.action_date).format("ddd") : null}</div>
        <div className="wf-feed-item-day">{data?.action_date ? dayjs(data.action_date).format("DD") : null}</div>
        <div className="wf-feed-item-month">{data?.action_date ? dayjs(data.action_date).format("MMM") : null}</div>
        <div className="wf-feed-item-year">{data?.action_date ? dayjs(data.action_date).format("YYYY") : null}</div>
      </div>

      {/* content */}
      <div className="wf-feed-item-content">
        {/* menu */}
        {(canEdit || canDelete) && data?.item_type === "comment" && (
          <MenuDropdown
            trigger="click"
            buttonLayout={"vertical"}
            items={menuItems}
            width={91}
            overlayClassName="wf-feed-item-menu-dropdown"
            className={`wf-feed-item-menu wf-feed-item-menu-vertical`}
          />
        )}
        {/* icon */}
        <div className="wf-feed-item-icon-wrap">
          <i
            className={data?.item_type === "comment" ? "fa-regular fa-message-lines" : "fa-solid fa-arrow-turn-right"}
          />
        </div>
        {/* eyebrow, title and main text */}
        {mainContent}
        {/* attachment */}
        {data?.attachment && data?.attachment_url && (
          <div
            className="wf-feed-item-content-attachment"
            onClick={() => setSelectedAttachment(data.attachment_url)}
          >
            <i className="fal fa-paperclip" />
            <a className="wf-feed-item-content-attachment_file">{data?.attachment.split("/").pop()}</a>
          </div>
        )}

        {/* tags */}
        {data?.tags && data.tags.length > 0 && (
          <div className="wf-feed-item-tags">
            {data.tags.map((tag, i) => (
              <Tag
                key={tag + i}
                text={tag}
                readOnly
              />
            ))}
          </div>
        )}
      </div>
      <Modal
        type="delete"
        title="Delete comment"
        message={"This action can't be undone."}
        open={openDeleteComment}
        onCancel={() => setOpenDeleteComment(false)}
        onOk={onDeleteComment}
        width={520}
        className="workflow-feed-item-modal"
      >
        <div className="workflow-feed-item-modal-content">{mainContent}</div>
      </Modal>
    </li>
  )
}

TimelineFeedItem.propTypes = {
  type: PropTypes.oneOf(["study", "activity", "request"]),
  data: PropTypes.shape({
    item_type: PropTypes.oneOf(["action", "comment"]),
    author_fullname: PropTypes.string,
    action_date: PropTypes.string,
    timeline_title: PropTypes.string,
    timeline_text: PropTypes.string,
    attachment: PropTypes.string,
    attachment_url: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    author_id: PropTypes.number,
  }),
  isLastItem: PropTypes.bool,
}

export default TimelineFeedItem
